/**
 * Shadcn Micro-Interactions - Phase 5 Animation System (OPTIMIZED)
 * Framer Motion-powered animations for Shadcn UI components
 * 
 * Performance Optimizations:
 * - Memoized variants (defined as constants outside render scope)
 * - GPU-accelerated transforms (translateZ, will-change removed to prevent overuse)
 * - Optimized spring physics for 60fps
 * - Named constants for magic numbers
 * - Tree-shakeable exports
 */

import { Variants, Transition } from 'framer-motion';

/* ===== ANIMATION CONSTANTS ===== */

/** Standard animation durations (in seconds) */
export const DURATION = {
  FAST: 0.15,
  NORMAL: 0.2,
  SLOW: 0.3,
  PAGE: 0.4,
} as const;

/** Easing curves optimized for perceived performance */
export const EASING = {
  SMOOTH: [0.4, 0, 0.2, 1] as const,
  BOUNCE: [0.68, -0.55, 0.265, 1.55] as const,
  EASE_OUT: [0, 0, 0.2, 1] as const,
} as const;

/** Spring physics configurations */
const SPRING_CONFIG = {
  SNAPPY: { stiffness: 400, damping: 25 },
  GENTLE: { stiffness: 300, damping: 30 },
  BOUNCY: { stiffness: 500, damping: 20 },
} as const;

/* ===== ANIMATION VARIANTS ===== */

/**
 * Button interaction variants - Optimized for desktop-first
 * Reduced scale changes for better performance
 */
export const buttonVariants: Variants = {
  initial: {
    scale: 1,
    y: 0,
  },
  hover: {
    scale: 1.02,
    y: -1,
    transition: {
      type: 'spring',
      ...SPRING_CONFIG.SNAPPY,
    },
  },
  tap: {
    scale: 0.98,
    y: 0,
    transition: {
      type: 'spring',
      ...SPRING_CONFIG.SNAPPY,
    },
  },
  loading: {
    opacity: 0.7,
    transition: {
      duration: DURATION.NORMAL,
    },
  },
  disabled: {
    opacity: 0.5,
    scale: 1,
  },
};

/**
 * Card hover and entrance variants - GPU-optimized
 */
export const cardVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.SLOW,
      ease: EASING.SMOOTH,
    },
  },
  hover: {
    y: -4,
    transition: {
      type: 'spring',
      ...SPRING_CONFIG.GENTLE,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: DURATION.FAST,
    },
  },
};

/**
 * Dropdown menu variants - Optimized entrance
 */
export const dropdownVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95,
    y: -10,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: DURATION.FAST,
      ease: EASING.EASE_OUT,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -10,
    transition: {
      duration: DURATION.FAST,
      ease: EASING.SMOOTH,
    },
  },
};

/**
 * List container for staggered children - Performance optimized
 */
export const listContainerVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03, // Reduced from 0.05 for faster perceived load
      delayChildren: 0,
    },
  },
};

/**
 * List item variants for stagger effect
 */
export const listItemVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -10,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: DURATION.FAST,
    },
  },
};

/**
 * Modal/Dialog variants - Optimized backdrop
 */
export const modalVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: DURATION.NORMAL,
      ease: EASING.EASE_OUT,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: DURATION.FAST,
    },
  },
};

/**
 * Backdrop overlay variants
 */
export const backdropVariants: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: DURATION.FAST,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: DURATION.FAST,
    },
  },
};

/**
 * Fade variants - Simple opacity transitions
 */
export const fadeVariants: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: DURATION.NORMAL,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: DURATION.FAST,
    },
  },
};

/**
 * Slide variants - Directional entrance
 */
export const slideVariants = {
  left: {
    initial: { x: -20, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: 20, opacity: 0 },
  },
  right: {
    initial: { x: 20, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: -20, opacity: 0 },
  },
  up: {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0 },
  },
  down: {
    initial: { y: -20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: 20, opacity: 0 },
  },
} as const;

/* ===== TRANSITION PRESETS ===== */

/**
 * Spring-based transitions
 */
export const springTransitions = {
  snappy: {
    type: 'spring' as const,
    ...SPRING_CONFIG.SNAPPY,
  },
  gentle: {
    type: 'spring' as const,
    ...SPRING_CONFIG.GENTLE,
  },
  bouncy: {
    type: 'spring' as const,
    ...SPRING_CONFIG.BOUNCY,
  },
};

/**
 * Easing-based transitions
 */
export const easingTransitions = {
  smooth: {
    duration: DURATION.NORMAL,
    ease: EASING.SMOOTH,
  },
  fast: {
    duration: DURATION.FAST,
    ease: EASING.SMOOTH,
  },
  slow: {
    duration: DURATION.SLOW,
    ease: EASING.SMOOTH,
  },
};

/* ===== UTILITY FUNCTIONS ===== */

/**
 * Check if user prefers reduced motion
 * Cached for performance
 */
let reducedMotionCache: boolean | null = null;

export const prefersReducedMotion = (): boolean => {
  if (reducedMotionCache !== null) return reducedMotionCache;
  
  if (typeof window === 'undefined') return false;
  
  reducedMotionCache = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return reducedMotionCache;
};

/**
 * Get variants with reduced motion support
 * Returns instant transitions if user prefers reduced motion
 */
export const getReducedMotionVariants = (variants: Variants): Variants => {
  if (!prefersReducedMotion()) return variants;

  const reducedVariants: Variants = {};
  Object.keys(variants).forEach((key) => {
    const variant = variants[key];
    if (typeof variant === 'object') {
      reducedVariants[key] = {
        ...variant,
        transition: { duration: 0 },
      };
    } else {
      reducedVariants[key] = variant;
    }
  });

  return reducedVariants;
};

/**
 * Get optimized transition based on motion preference
 */
export const getMotionTransition = (transition: Transition): Transition => {
  if (!prefersReducedMotion()) return transition;
  return { duration: 0 };
};

/**
 * Stagger configuration generator
 */
export const createStaggerConfig = (
  staggerDelay: number = 0.03,
  delayChildren: number = 0
) => ({
  staggerChildren: prefersReducedMotion() ? 0 : staggerDelay,
  delayChildren: prefersReducedMotion() ? 0 : delayChildren,
});
